<template>
  <div v-if="system && system.homepage === null">
    <page-header
      header="Dashboard"
      subheader="A quick reference place for any notifications regarding News, Feeds and Offers so check back regularly for updates."
    />

    <div class="flex flex-wrap justify-around -mx-5" v-if="(selectedOffice.permissions && selectedOffice.permissions.includes('referrals only') === false)">

      <!-- PROPERTIES -->
      <div class="md:flex-1 flex-col w-full mx-5 mb-6 lg:mb-0 lg:flex-1/2 bg-white rounded-lg">
        <div class="p-6 bg-secondary text-white rounded-t-lg flex items-center">
          <h2 class="font-semibold">Active Properties</h2>
        </div>
        <div class="flex py-2 px-6">
          <div class="w-full flex flex-col" v-if="activeProperties && activeProperties.hasOwnProperty('properties')">
            <div class="flex flex-col">
              <h2 class="text-5xl font-semibold">
                {{ activeProperties.properties.active }}
              </h2>
              <h4 class="text-lg font-medium leading-tight">
                Active Properties
              </h4>
              <small v-if="profile.type <= 2" class="leading-tight">
                {{ selectedOffice.name }}
              </small>
            </div>
            <div class="w-full mt-6">
              <div class="flex justify-between items-center">
                <span class="w-1/4">Sales</span>
                <span>{{ activeProperties.properties.sales }}</span>
                <div v-if="activeProperties.properties.alerts === 0" class="tag-bubble-inline success-tag">
                  <i class="fas fa-check" />
                </div>
                <div v-else class="tag-bubble-inline alert-tag">
                  <i class="fas fa-exclamation" />
                </div>
                <div>
                  <span class="ml-2 fa">
                    <i :class="[expand_sales ? 'fa-chevron-up' : 'fa-chevron-down']" @click.stop.prevent="expand_sales = !expand_sales"></i>
                  </span>
                </div>
              </div>
              <div v-if="expand_sales" class="mt-4">
                <div  v-for="(count, key) in items.active_properties_count.sales.break_down" :key="key">
                  <div class="flex justify-between items-center" v-if="count">
                    <span class="mb-2 text-sm">{{ key }}: </span>
                    <span class="text-sm">{{ count }}</span>
                  </div>
                </div>
              </div>
              <div class="border-t my-2"></div>
              <div class="flex justify-between items-center">
                <span class="w-1/4">Lettings</span>
                <span>{{ activeProperties.properties.rentals }}</span>
                <div v-if="activeProperties.properties.alerts === 0" class="tag-bubble-inline success-tag">
                  <i class="fas fa-check" />
                </div>
                <div v-else class="tag-bubble-inline alert-tag">
                  <i class="fas fa-exclamation" />
                </div>
                <div>
                  <span class="ml-2 fa">
                    <i :class="[expand_letting ? 'fa-chevron-up' : 'fa-chevron-down']" @click.stop.prevent="expand_letting = !expand_letting"></i>
                  </span>
                </div>
              </div>
              <div v-if="expand_letting" class="mt-4">
                <div  v-for="(count, key) in items.active_properties_count.lettings.break_down" :key="key">
                  <div class="flex justify-between items-center" v-if="count">
                    <span class="mb-2 text-sm">{{ key }}: </span>
                    <span class="text-sm">{{ count }}</span>
                  </div>
                </div>
              </div>
              <div class="border-t my-2"></div>
              <div v-if="selectedOffice.network_id !== 8">
                <div class="flex justify-between items-center">
                  <span class="w-1/4">Rejected Properties
                  </span>
                  <span>{{activeProperties.properties.rejected}}</span>
                  <div  class="text-red">
                    <i class="fas fa-exclamation-triangle" />
                  </div>
                </div>
                <small class="leading-tight font-small">POA is no longer a valid price qualifier</small>
              </div>
            </div>
            <div v-if="activeProperties.properties.alerts > 0" class="mt-auto mb-4 flex items-center text-sm">
              <div class="tag-bubble-inline alert-tag mr-4">
                <i class="fas fa-exclamation fa-fw" />
              </div>
              <span class="border-b border-red-light pb-1 text-red-light">
                {{ activeProperties.properties.alerts }} Require Attention
              </span>
            </div>
          </div>
          <loader v-else class="self-center mx-auto" />
        </div>
      </div> <!-- end of active properties -->

      <!-- EVENTS -->
      <div class="w-full mx-5 mb-6 mt-8 xl:mt-0 xl:mb-0 xl:flex-2 bg-white rounded-lg">
        <calendar-widget class="bg-tertiary h-full hidden lg:block">
          <template slot="header">
            <h3 class="text-white text-center w-full text-xl font-medium"><span v-if="system && system.name">{{ system.name }}</span> Calendar</h3>
          </template>
        </calendar-widget>
        <event-list-widget class="bg-tertiary block lg:hidden" />
      </div> <!-- end of events -->
    </div>

    <div class="flex flex-wrap justify-around -mx-5 xl:mt-8" v-if="(selectedOffice.permissions && selectedOffice.permissions.includes('referrals only') === false)">
      <!-- NOTIFICATIONS -->
      <div class="flex-1 w-full mx-5 mb-6 lg:mb-0 lg:flex-2 bg-white rounded-lg">
        <div class="p-6 bg-primary text-white rounded-t-lg flex items-center">
          <h2 class="font-semibold">Notifications</h2>
          <i class="fas fa-bell ml-6" />
        </div>
        <div class="flex flex-1 flex-col min-h-32">
          <div class="w-full text-xl flex-row p-4">
            <span class="text-red-dark text-2xl font-bold" v-html="unreadNotifications"></span> New Notifications
          </div>
          <notification-list :limit="3" class="p-4 h-full w-full flex-row" />
        </div>
      </div> <!-- end notifications -->
      <div class="w-full mx-5 mb-6 lg:mb-0 lg:flex-2 rounded-lg bg-white">
        <div class="p-6 bg-primary text-white rounded-t-lg flex items-center">
          <h2 class="font-semibold">News</h2>
        </div>
        <div class="flex-1 justify-center items-center min-h-32 p-4">
          <div v-for="(article, index) in news" :key="index" class="flex items-center w-full mb-3" :class="{ 'border-t pt-3': index > 0 }">
            <!-- <pre class="code">{{ article }}</pre> -->
            <router-link to='news' class="no-underline text-black w-full">
              <div v-if="article.website_url" class="mr-4 w-32 h-16 bg-center bg-cover bg-no-repeat" :style="{ backgroundImage: `url(${article.website_url})` }"></div>
              <div class="flex flex-col">
                <span class="leading-tight text-lg font-medium mb-1">{{ article.title }}</span>
                <span class="leading-tight text-xs text-grey-dark font-medium">{{ article.updated_at | verboseDate }}</span>
              </div>
            </router-link>
          </div>
          <router-link to="/news" class="text-xs text-grey-darker font-medium text-center m-0 mt-auto p-0 w-full block">
            View All
          </router-link>
        </div>
      </div> <!-- end of notifications -->
      <div class="flex flex-col w-full mx-5 mb-6 lg:mb-0 lg:flex-1/2 rounded-lg bg-white">
        <network-alerts />
      </div> <!-- end of alerts -->
    </div>
    <div class="flex flex-wrap justify-around -mx-5 xl:mt-8">
      <!-- Tiles -->
      <div class="mx-5 mt-10 mb-16 xl:my-0 xl:flex-2 w-full rounded-lg">
        <!-- mx-5 mb-6 xl:mb-0 -->
        <sortable-tiles />
      </div> <!-- end of tiles -->
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import NetworkAlerts from '@/components/NetworkAlerts'
import SortableTiles from '@/components/Dashboard/SortableTiles'
import { mapState } from 'vuex'
import api from '@/http'
export default {
  name: 'home',
  data: function () {
    return {
      hasDispatched: false,
      items: [],
      expand_letting: false,
      expand_sales: false
    }
  },
  components: {
    NetworkAlerts,
    SortableTiles
  },
  methods: {
    fetchItems () {
      api.get(`/feed-stats/${this.selectedOffice.id}`)
        .then((response) => {
          this.items = response.data
        })
        .finally(() => {
        })
    }
  },
  computed: {
    ...mapState(['activeProperties', 'selectedOffice', 'profile', 'articles', 'system']),
    news () {
      return this.articles.filter(a => {
        if ([7, 8].includes(this.selectedOffice.network_id) && a.assigned_countries.length > 0 && a.assigned_countries.includes('UKA')) {
          return (a.assigned_network === this.selectedOffice.network_id || a.assigned_network == null) && (this.selectedOffice.associate === true || a.assigned_countries.includes(this.selectedOffice.country))
        } else if ([7, 8].includes(this.selectedOffice.network_id) && a.assigned_countries.length > 0) {
          return (a.assigned_network === this.selectedOffice.network_id || a.assigned_network == null) && (a.assigned_countries.includes(this.selectedOffice.country))
        }
        return a.assigned_network === this.selectedOffice.network_id || a.assigned_network == null
      }).splice(0, 3)
    },
    unreadNotifications () {
      if (this.profile.notifications !== undefined) {
        return this.profile.notifications.filter(n => n.read_at == null).length
      } else {
        return 0
      }
    }
  },
  created () {
    if (this.system.homepage !== null) {
      this.$router.replace(this.system.homepage)
      return
    }
    if (this.selectedOffice.id) {
      this.$store.dispatch('fetchActiveProperties', this.selectedOffice.id)
      this.fetchItems()
    }
  },
  watch: {
    profile: function (val) {
      // this.$store.dispatch('fetchActiveProperties', selectedOffice.id)
      console.info('Profile changed')
    },
    selectedOffice: function (val, prev) {
      if (val.id !== prev.id) {
        this.$store.dispatch('fetchActiveProperties', val.id)
        this.fetchItems()
      }
    }
  }
}
</script>
